import React, { useState } from 'react';
import { palettes, LargeTitle, Select, Button, useFlashMessage } from '@indigo-ag/component-module';
import { ApolloQueryResult, ApolloError, useMutation } from '@apollo/client';
import i18next from 'core/i18n';

import GrowerReportUploadModal from '../../components/GrowerReportUploadModal/GrowerReportUploadModal';
import {
  financingBrazilDatasets as financingBrazilDatasetsQuery,
  financingBrazilKMLs as financingBrazilKMLsQuery,
  importFinancingGrowerReport
} from '../../queries';
import { NewPage } from 'core/components';
import {
  EnhancedPaper,
  createStylesHook,
  LoadingWrapper,
  CardTableInfo,
  CardTable,
  FinancingBrazilDataset,
  FinancingBrazilKML,
  NoResultsFound
} from 'shared';
import { GrowerKMLList } from 'features/upload-grower-data/components/GrowerKMLList/GrowerKMLList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const useStyles = createStylesHook({
  datasetDetail: {
    '& div': {
      '& h6,p': {
        display: 'inline-block'
      },
      '& p': {
        marginLeft: 8
      },
      marginTop: 16
    }
  },
  mb32: {
    marginBottom: 32
  },
  paper: {
    marginBottom: 16,
    maxHeight: '80vh',
    overflow: 'scroll'
  },
  uploadLink: {
    color: palettes.legacy.blue.default,
    cursor: 'pointer',
    fontSize: '1rem'
  }
});

interface GrowerReportUploadComponentProps {
  financingBrazilDatasets: FinancingBrazilDataset[];
  financingBrazilKMLs: FinancingBrazilKML[];
  financingBrazilDatasetsRefetch: (variables?: Record<string, any> | undefined) => Promise<ApolloQueryResult<any>>;
}

const GrowerReportUploadComponent: React.FunctionComponent<GrowerReportUploadComponentProps> = ({
  financingBrazilDatasets,
  financingBrazilKMLs,
  financingBrazilDatasetsRefetch
}) => {
  /**
   * State
   */
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedDatasetId, setSelectedDatasetId] = useState<FinancingBrazilDataset['id'] | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);

  /**
   * Styles
   */
  const classes = useStyles();

  /**
   * Mutations
   */
  const [importFinancingGrowerReportFn] = useMutation(importFinancingGrowerReport);

  const openModal = () => {
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
  };

  const updateRemoteDataSetDocument = (newFile: FinancingBrazilDataset) => {
    financingBrazilDatasetsRefetch().then(() => {
      setSelectedDatasetId(newFile.id);
    });
  };

  const { addFlashMessage } = useFlashMessage();

  const deployDataSet = async () => {
    setIsUpdating(true);

    await importFinancingGrowerReportFn({
      variables: {
        fileId: Number(selectedDatasetId)
      }
    }).then(res => {
      if (res.data.importFinancingGrowerReport) {
        addFlashMessage({
          content: 'Dataset successfully update',
          title: '',
          variant: 'success'
        });
        setIsUpdating(false);
      } else {
        addFlashMessage({
          content: 'Dataset failed to update',
          title: '',
          variant: 'error'
        });
        setIsUpdating(false);
      }
    });
  };

  const ContentPaper: React.FunctionComponent<{
    title: string;
    uploadText: string;
    children: JSX.Element;
    overflowVisible?: boolean;
  }> = ({ title, uploadText, overflowVisible = false, children }) => {
    return (
      <EnhancedPaper className={classes.paper} style={{ overflow: overflowVisible ? 'visible' : 'scroll' }}>
        <div className="flex flex-column">
          <div className={`flex flex-space-btw ${classes.mb32}`}>
            <LargeTitle>{title}</LargeTitle>
            <span className={classes.uploadLink} onClick={openModal}>
              <FontAwesomeIcon icon="upload" style={{ paddingRight: 8 }} />
              {uploadText}
            </span>
          </div>
          {children}
        </div>
      </EnhancedPaper>
    );
  };

  const renderDatasetDetails = () => {
    if (selectedDatasetId) {
      const selectedDatasetInfo = financingBrazilDatasets.find(d => d.id === selectedDatasetId);

      const datasetInfoRows: CardTableInfo[] = !selectedDatasetInfo
        ? []
        : [
            { title: i18next.t('documentName'), value: selectedDatasetInfo.documentName },
            { title: i18next.t('documentUrl'), value: selectedDatasetInfo.documentUrl }
          ];

      return <CardTable info={datasetInfoRows} />;
    } else {
      return null;
    }
  };

  const renderDatasetList = () => {
    const datasetsOptions = financingBrazilDatasets.map(dataset => ({
      name: dataset.documentName,
      value: dataset.id
    }));

    // * filterable and disableNative props of Select trigger a Warning error on the console
    return (
      <ContentPaper
        title={i18next.t('dataset')}
        uploadText={`${i18next.t('finish')} ${i18next.t('dataset')}`}
        overflowVisible
      >
        <div className="flex flex-column">
          <div className="flex flex-space-btw">
            <div style={{ width: '100%', marginRight: 16 }}>
              <Select
                filterable
                label={i18next.t('dataset')}
                hideLabel
                name="datasets"
                options={datasetsOptions}
                value={selectedDatasetId}
                onSelectionChanged={setSelectedDatasetId}
                maxContentHeight={300}
              />
            </div>
            <Button disabled={!selectedDatasetId} busy={isUpdating} onClick={deployDataSet}>
              {i18next.t('deploy')}
            </Button>
          </div>
          {renderDatasetDetails()}
        </div>
      </ContentPaper>
    );
  };

  const renderKMLList = () => {
    return (
      <ContentPaper title={i18next.t('kmlFiles')} uploadText={`${i18next.t('finish')} ${i18next.t('kmlFiles')}`}>
        <GrowerKMLList financingBrazilKMLs={financingBrazilKMLs} />
      </ContentPaper>
    );
  };

  return (
    <NewPage title={i18next.t('growerDataSet')}>
      <div className="flex flex-column" style={{ flexWrap: 'wrap' }}>
        <div style={{ marginBottom: '0', flexGrow: 1, flexShrink: 1, flexBasis: 0 }}>{renderDatasetList()}</div>
        <div style={{ marginBottom: '0', minWidth: '212px', flexGrow: 1, flexShrink: 1, flexBasis: 0 }}>
          {renderKMLList()}
        </div>
      </div>
      <GrowerReportUploadModal
        closeModal={closeModal}
        open={modalOpen}
        updateRemoteDataSetDocument={updateRemoteDataSetDocument}
      />
    </NewPage>
  );
};

export const GrowerReportUpload: React.FunctionComponent = () => {
  const { addFlashMessage } = useFlashMessage();

  return (
    <LoadingWrapper query={financingBrazilDatasetsQuery} queryOptions={{ errorPolicy: 'all', fetchPolicy: 'no-cache' }}>
      {({
        data,
        error,
        refetch: financingBrazilDatasetsRefetch
      }: {
        data: { financingBrazilDatasets: FinancingBrazilDataset[] };
        error?: ApolloError;
        refetch: (variables?: Record<string, any> | undefined) => Promise<ApolloQueryResult<any>>;
      }) => {
        if (error) {
          addFlashMessage({
            content: i18next.t('error'),
            title: '',
            variant: 'error'
          });
        }

        if (!data) {
          return (
            <div style={{ paddingTop: '15%' }}>
              <NoResultsFound />
            </div>
          );
        }
        const financingBrazilDatasets = data.financingBrazilDatasets;

        return (
          <LoadingWrapper query={financingBrazilKMLsQuery}>
            {({ data }: { data: { financingBrazilKMLs: FinancingBrazilKML[] } }) => {
              const financingBrazilKMLs = data.financingBrazilKMLs;

              return (
                <GrowerReportUploadComponent
                  financingBrazilDatasets={financingBrazilDatasets}
                  financingBrazilKMLs={financingBrazilKMLs}
                  financingBrazilDatasetsRefetch={financingBrazilDatasetsRefetch}
                />
              );
            }}
          </LoadingWrapper>
        );
      }}
    </LoadingWrapper>
  );
};
